import type { IEventRepository } from "@/core/repositories/interfaces/IEventRepository";
import type { IMediaService } from "@/core/services/interfaces/IMediaService";
import type { IEventService } from "@/core/services/interfaces/IEventService";
import type {
  CreateEventInput,
  CreateEventResult,
  Event,
  EventEditor,
  EventLibraryKpis,
  EventLibraryTab,
  EventLibraryTabCounts,
  EventLibraryCategoryFilter,
  EventLibraryCategoryCounts,
  EventLibraryDateFilters,
  EventLibrarySortState,
  EventListPage,
  EventAdminExportItem,
  EventSummary,
  EventTaxonomy,
  UpdateEventAssignmentInput,
  UpdateEventInternalNotesInput,
  UpdateEventContentInput,
  UpdateEventCoverImageInput,
  UpdateEventPlaceInput,
  UpdateEventStatusInput,
  UpdateEventTaxonomyInput,
  UpdateEventTaxonomyResult,
  UploadEventCoverImageResult,
} from "@/core/services/types/event.types";
import {
  isDeletableEditorialStatus,
  type DeleteSignalContentResult,
} from "@/core/services/types/article.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type { PlaceLinkedEvent } from "@/core/services/types/place.types";

export class EventService implements IEventService {
  constructor(
    private readonly repository: IEventRepository,
    private readonly mediaService: IMediaService,
  ) {}

  async createEvent(input: CreateEventInput): Promise<CreateEventResult> {
    if (input.status === "SIGNAL") {
      const { status: _status, ...createInput } = input;
      const created = await this.repository.createAdmin({
        ...createInput,
        status: "DRAFT",
      });

      if (!created.success) {
        return created;
      }

      const promoted = await this.repository.updateAdminStatus(created.data.id, {
        editStatus: "SIGNAL",
      });

      if (!promoted) {
        return { success: false, error: "signal_status_failed" };
      }

      return { success: true, data: promoted };
    }

    return this.repository.createAdmin(input);
  }

  async deleteSignalContent(id: string): Promise<DeleteSignalContentResult> {
    if (!id.trim()) {
      return { success: false, error: "not_found" };
    }

    const event = await this.repository.findAdminById(id);
    if (!event) {
      return { success: false, error: "not_found" };
    }

    if (!isDeletableEditorialStatus(event.status)) {
      return { success: false, error: "not_deletable" };
    }

    const deleted = await this.repository.deleteAdmin(id);
    if (!deleted) {
      return { success: false, error: "delete_failed" };
    }

    return { success: true };
  }

  async listPublished(limit?: number): Promise<EventSummary[]> {
    return this.repository.findPublished(limit);
  }

  async getById(id: string): Promise<Event | null> {
    if (!id.trim()) return null;
    return this.repository.findById(id);
  }

  async getForEdit(id: string): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.findAdminById(id);
  }

  async updateEventContent(
    id: string,
    input: UpdateEventContentInput,
  ): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminContent(id, input);
  }

  async updateEventStatus(
    id: string,
    input: UpdateEventStatusInput,
  ): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminStatus(id, input);
  }

  async updateEventAssignment(
    id: string,
    input: UpdateEventAssignmentInput,
  ): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminAssignment(id, input);
  }

  async updateEventInternalNotes(
    id: string,
    input: UpdateEventInternalNotesInput,
  ): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminInternalNotes(id, input);
  }

  async updateEventCoverImage(
    id: string,
    input: UpdateEventCoverImageInput,
  ): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminCoverImage(id, input);
  }

  async updateEventPlace(
    id: string,
    input: UpdateEventPlaceInput,
  ): Promise<EventEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminPlace(id, input);
  }

  async uploadEventCoverImage(
    id: string,
    file: File,
  ): Promise<UploadEventCoverImageResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing event id" };
    }

    const uploadResult = await this.mediaService.uploadEventCoverImage(id, file);

    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error };
    }

    const updated = await this.repository.updateAdminCoverImage(id, {
      coverImageUrl: uploadResult.publicUrl,
    });

    if (!updated) {
      return {
        success: false,
        error: "Failed to save cover image URL",
      };
    }

    return { success: true, data: updated };
  }

  async listByLibraryTab(
    tab: EventLibraryTab,
    page: number,
    pageSize: LibraryPageSize,
    search?: string,
    category: EventLibraryCategoryFilter = "ALL",
    dateFilters?: EventLibraryDateFilters,
    sort?: EventLibrarySortState,
  ): Promise<EventListPage> {
    return this.repository.findByLibraryTab(
      tab,
      page,
      pageSize,
      search,
      category,
      dateFilters,
      sort,
    );
  }

  async listAllByLibraryTab(
    tab: EventLibraryTab,
    search?: string,
    category: EventLibraryCategoryFilter = "ALL",
    dateFilters?: EventLibraryDateFilters,
  ): Promise<EventAdminExportItem[]> {
    return this.repository.findAllByLibraryTab(
      tab,
      search,
      category,
      dateFilters,
    );
  }

  async countByLibraryTab(
    search?: string,
    category: EventLibraryCategoryFilter = "ALL",
    dateFilters?: EventLibraryDateFilters,
  ): Promise<EventLibraryTabCounts> {
    return this.repository.countByLibraryTab(search, category, dateFilters);
  }

  async countByLibraryCategory(): Promise<EventLibraryCategoryCounts> {
    return this.repository.countByLibraryCategory();
  }

  async getLibraryStats(): Promise<{
    tabCounts: EventLibraryTabCounts;
    categoryCounts: EventLibraryCategoryCounts;
    kpis: EventLibraryKpis;
  }> {
    const [tabCounts, categoryCounts, publishedThisMonth] = await Promise.all([
      this.repository.countByLibraryTab(),
      this.repository.countByLibraryCategory(),
      this.repository.countPublishedThisMonth(),
    ]);

    return {
      tabCounts,
      categoryCounts,
      kpis: {
        total: tabCounts.ALL,
        pendingReview: tabCounts.PENDING_REVIEW,
        publishedThisMonth,
      },
    };
  }

  async listByPlaceId(placeId: string): Promise<PlaceLinkedEvent[]> {
    if (!placeId.trim()) return [];
    return this.repository.findAdminByPlaceId(placeId);
  }

  async getEventTaxonomy(id: string): Promise<EventTaxonomy> {
    if (!id.trim()) {
      return { artFormIds: [], artisticPeriodIds: [], culturalAreaIds: [], exhibitionTypeIds: [] };
    }
    return this.repository.findTaxonomy(id);
  }

  async updateEventTaxonomy(
    id: string,
    input: UpdateEventTaxonomyInput,
  ): Promise<UpdateEventTaxonomyResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing event id" };
    }

    const ok = await this.repository.replaceTaxonomy(id, input);

    if (!ok) {
      return { success: false, error: "Failed to update taxonomy" };
    }

    return { success: true };
  }
}
